import { useEffect, useRef, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { t } from "@/lib/i18n"
import { Spinner } from "@/components/ui"

export const Route = createFileRoute("/_app/search")({
  component: SearchPage,
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({ q: typeof s.q === "string" ? s.q : undefined }),
})

type Hit = {
  kind: "conversation" | "memory" | "knowledge" | "mail"
  title: string
  snippet: string
  url?: string | null
  when?: string | null
  score?: number | null
  meta: Record<string, unknown>
}
type SearchOut = {
  q: string
  conversations: Hit[]
  memories: Hit[]
  knowledge: Hit[]
  mail: Hit[]
  mail_sources: string[]
  took_ms: number
}

/** One search box over conversations, memories, documents and mail — every
 *  hit cites where it came from and opens there. */
function SearchPage() {
  const { q: initial } = Route.useSearch()
  const navigate = useNavigate()
  const [q, setQ] = useState(initial ?? "")
  const [submitted, setSubmitted] = useState(initial ?? "")
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])
  useEffect(() => { if (initial !== undefined && initial !== submitted) { setQ(initial); setSubmitted(initial) } }, [initial]) // eslint-disable-line react-hooks/exhaustive-deps

  const results = useQuery({
    queryKey: ["search", submitted],
    queryFn: () => api<SearchOut>("/api/search", { query: { q: submitted, limit: 8 } }),
    enabled: submitted.trim().length > 0,
    staleTime: 60_000,
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const v = q.trim()
    if (!v) return
    setSubmitted(v)
    void navigate({ to: "/search", search: { q: v } })
  }

  const r = results.data
  const total = r ? r.conversations.length + r.memories.length + r.knowledge.length + r.mail.length : 0

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[30px] leading-none">{t("search.title")}</h1>
        <p className="text-[13px] text-muted-foreground">{t("search.sub")}</p>
      </div>

      <form onSubmit={submit} className="flex items-center gap-2">
        <label className="flex h-12 flex-1 items-center gap-3 rounded-full bg-card px-4 text-muted-foreground ring-1 ring-inset ring-border focus-within:ring-primary/50">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder")}
            className="w-full bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground"
          />
        </label>
        <button type="submit" className="h-12 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50" disabled={!q.trim()}>
          {t("search.go")}
        </button>
      </form>

      {results.isFetching && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> {t("search.searching")}</div>
      )}
      {r && !results.isFetching && (
        <div className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
          {total} {t("search.hits")} · {r.took_ms} ms{r.mail_sources.length ? ` · mail: ${r.mail_sources.join(", ")}` : ""}
        </div>
      )}
      {r && total === 0 && !results.isFetching && (
        <div className="text-sm text-muted-foreground">{t("search.none")}</div>
      )}

      {r && total > 0 && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Section title={t("search.conversations")} hits={r.conversations} q={submitted} />
          <Section title={t("search.memories")} hits={r.memories} q={submitted} />
          <Section title={t("search.knowledge")} hits={r.knowledge} q={submitted} />
          <Section title={t("search.mail")} hits={r.mail} q={submitted} />
        </div>
      )}
    </div>
  )
}

const DOT: Record<Hit["kind"], string> = {
  conversation: "bg-primary",
  memory: "bg-violet",
  knowledge: "bg-ember",
  mail: "bg-mint",
}

function Section({ title, hits, q }: { title: string; hits: Hit[]; q: string }) {
  if (hits.length === 0) return null
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-baseline gap-3">
        <div className="font-display text-[18px]">{title}</div>
        <div className="font-mono text-[11px] text-muted-foreground">{hits.length}</div>
      </div>
      <ul className="flex flex-col gap-2">
        {hits.map((h, i) => {
          const internal = h.url?.startsWith("/")
          const body = (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[h.kind]}`} />
                  <span className="truncate text-[14px] font-medium text-foreground">{h.title}</span>
                </div>
                {h.when && (
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    {new Date(h.when).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                  </span>
                )}
              </div>
              {h.snippet && <p className="mt-1.5 line-clamp-3 text-[13px] leading-snug text-foreground/80"><Mark text={h.snippet} q={q} /></p>}
              <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                {h.kind === "conversation" ? t("search.citeConversation")
                  : h.kind === "memory" ? `${t("search.citeMemory")} · ${String(h.meta.kind ?? "").replace(/_/g, " ")}`
                  : h.kind === "knowledge" ? `${t("search.citeKnowledge")} · ${String(h.meta.file_path ?? "")}${h.meta.line_start ? `:${h.meta.line_start}` : ""}`
                  : `${t("search.citeMail")} · ${String(h.meta.source ?? "")}`}
              </div>
            </>
          )
          const cls = "block rounded-2xl bg-card p-3.5 ring-1 ring-inset ring-border transition hover:ring-primary/40"
          if (h.url && internal) {
            const [path, qs] = h.url.split("?")
            const search = Object.fromEntries(new URLSearchParams(qs ?? ""))
            return <li key={i}><Link to={path} search={search} className={cls}>{body}</Link></li>
          }
          if (h.url) return <li key={i}><a href={h.url} target="_blank" rel="noreferrer" className={cls}>{body}</a></li>
          return <li key={i}><div className={cls}>{body}</div></li>
        })}
      </ul>
    </section>
  )
}

function Mark({ text, q }: { text: string; q: string }) {
  const needle = q.trim()
  if (!needle) return <>{text}</>
  const i = text.toLowerCase().indexOf(needle.toLowerCase())
  if (i < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded bg-primary/25 px-0.5 text-foreground">{text.slice(i, i + needle.length)}</mark>
      {text.slice(i + needle.length)}
    </>
  )
}
